import React from 'react';
import { Clock, User, Bot, CheckCircle2, MessageSquare, Download } from 'lucide-react';

const ActivityFeed = ({ activities = [], canExport = false, onExport }) => {
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  const truncateText = (text, maxLength = 80) => {
    if (!text) return 'No message';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  return (
    <div className="rounded-[20px] border bg-white dark:bg-white/[0.02] border-gray-200 dark:border-white/[0.06] overflow-hidden">
      <div className="px-5 sm:px-6 py-4 sm:py-5 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Recent Activity</h3>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-500 font-light">Latest customer interactions</p>
        </div>
        {canExport && (
          <button
            onClick={onExport}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-white/[0.08] dark:text-gray-300 dark:hover:bg-white/[0.04] shrink-0"
            title="Download all conversation transcripts as CSV"
          >
            <Download className="w-3.5 h-3.5" />
            Export CSV
          </button>
        )}
      </div>
      <div className="px-5 sm:px-6 pb-5 sm:pb-6">
        {!activities || activities.length === 0 ? (
          <div className="flex items-center justify-center py-12 sm:py-16">
            <div className="text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-[14px] bg-gray-50 dark:bg-white/[0.04] flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <MessageSquare className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400 dark:text-gray-500" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-1.5 sm:mb-2">No activity yet</h3>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 max-w-xs px-4">
                Customer interactions will appear here
              </p>
            </div>
          </div>
        ) : (
          <div
            className="space-y-4 sm:space-y-6 max-h-[350px] sm:max-h-[420px] overflow-y-auto pr-1 sm:pr-2"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#cbd5e0 transparent'
            }}
          >
            {activities.map((activity) => (
              <div key={activity.id} className="space-y-2 sm:space-y-3">
                {/* Timestamp Header */}
                <div className="flex items-center justify-between px-0.5 sm:px-1">
                  <div className="flex items-center gap-1.5 sm:gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <Clock className="w-3 h-3 flex-shrink-0" />
                    <span>{formatTimestamp(activity.created_at)}</span>
                  </div>
                  {activity.metadata?.status === 'resolved' && (
                    <div className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                      <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
                      <span className="hidden xs:inline">Resolved</span>
                    </div>
                  )}
                </div>

                {/* Customer Question - Right aligned */}
                <div className="flex items-start gap-2 sm:gap-3 justify-end">
                  <div className="max-w-[90%] sm:max-w-[85%]">
                    <div className="bg-black dark:bg-white text-white dark:text-black rounded-[14px] rounded-tr-sm px-3 py-2 sm:px-4 sm:py-3 shadow-sm">
                      <p className="text-xs sm:text-sm leading-relaxed">
                        {truncateText(activity.user_query, 60)}
                      </p>
                    </div>
                  </div>
                  <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gray-100 dark:bg-white/[0.06] flex items-center justify-center">
                    <User className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 dark:text-gray-400" />
                  </div>
                </div>

                {/* AI Response - Left aligned */}
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gray-100 dark:bg-white/[0.06] flex items-center justify-center">
                    <Bot className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 dark:text-gray-400" />
                  </div>
                  <div className="max-w-[90%] sm:max-w-[85%]">
                    <div className="bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-gray-100 rounded-[14px] rounded-tl-sm px-3 py-2 sm:px-4 sm:py-3 shadow-sm">
                      <p className="text-xs sm:text-sm leading-relaxed">
                        {truncateText(activity.ai_response, 60)}
                      </p>
                    </div>
                    {activity.response_time && activity.response_time <= 300000 && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 sm:mt-1 ml-1">
                        Responded in {Math.round(activity.response_time / 1000)}s
                      </p>
                    )}
                  </div>
                </div>

                {/* Divider between conversations */}
                {activities.indexOf(activity) < activities.length - 1 && (
                  <div className="border-t border-gray-100 dark:border-white/[0.04] pt-2 sm:pt-3" />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityFeed;
